import {
  formatInternational,
  getCountryCodeFromPhoneNumber,
} from 'lib-shared-phone-numbers';
import { useCallback, useEffect, useMemo } from 'react';

import FlagSelect, {
  ItemT,
} from 'components/settings/components/sections/telephony/flagSelect';
import { withPhoneNumbers, WithPhoneNumbersInjectedProps } from 'hocs';

interface Props extends WithPhoneNumbersInjectedProps {
  selectedNumber: string | undefined;
  onSelect: (number: string) => void;
  disabled?: boolean;
}

function NumberSelect({
  phoneNumbers,
  selectedNumber,
  onSelect,
  disabled,
}: Props): JSX.Element {
  const {
    getItems,
    getItemsAsyncStatus,
    hasNextPage,
    items,
    nextPage,
    paramsChange,
  } = phoneNumbers;

  const isLoading = getItemsAsyncStatus === 'loading';
  const handleOnSelect = useCallback(
    ({ id }: ItemT) => {
      onSelect(id);
    },
    [onSelect],
  );

  const handleSearch = useCallback(
    (query: string) => {
      paramsChange({ search: query });
    },
    [paramsChange],
  );

  const options = useMemo(
    () =>
      items.map(({ e164_format, name }) => {
        const flag = getCountryCodeFromPhoneNumber(e164_format) ?? '';
        const number = formatInternational(e164_format);

        return {
          id: e164_format,
          flag,
          name: `${number} ${name ? `(${name})` : ''}`,
        };
      }),
    [items],
  );

  useEffect(() => {
    getItems();
  }, [getItems]);

  return (
    <FlagSelect
      disabled={disabled}
      isLoading={isLoading && !items.length}
      items={options}
      onSelect={handleOnSelect}
      pagination={{
        infinite: true,
        nextEnable: !!hasNextPage,
        previousEnable: false,
        onNext: nextPage,
      }}
      placeholder="Search for e.g. Support or a phone number"
      selectedItem={selectedNumber}
      onSearch={handleSearch}
    />
  );
}

export default withPhoneNumbers(NumberSelect, {
  infinite: true,
});
